import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import {
  FileText, CheckCircle2, XCircle, MessageSquare, Clock, Filter,
  Building2, TrendingUp, Send, ArrowLeft, RefreshCw, AlertTriangle,
  LogOut, Search
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;
const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n || 0));
const STATUS_MAP = {
  recue: { label: "Recue", cls: "bg-blue-500/15 text-blue-400" },
  received: { label: "Recue", cls: "bg-blue-500/15 text-blue-400" },
  deposee: { label: "Déposée", cls: "bg-sky-500/15 text-sky-400" },
  submitted: { label: "Déposée", cls: "bg-sky-500/15 text-sky-400" },
  en_recouvrement: { label: "Recouvrement", cls: "bg-amber-500/15 text-amber-400" },
  payee: { label: "Validée", cls: "bg-emerald-500/15 text-emerald-400" },
  paiement_partiel: { label: "Partiel", cls: "bg-amber-500/15 text-amber-400" },
  rejetee: { label: "Rejetée", cls: "bg-red-500/15 text-red-400" },
  complement_requis: { label: "Complement requis", cls: "bg-orange-500/15 text-orange-400" },
};

function InvoiceDetailModal({ invoice, onClose, onAction, onSendMessage }) {
  const [action, setAction] = useState("");
  const [motif, setMotif] = useState("");
  const [msgContent, setMsgContent] = useState("");
  const [messages, setMessages] = useState([]);
  const [tab, setTab] = useState("detail");

  useEffect(() => {
    if (invoice && tab === "messages") {
      axios.get(`${API}/insurance/messages/${invoice.id}`, { headers: getHeaders() })
        .then(res => setMessages(res.data.messages || []))
        .catch(() => {});
    }
  }, [invoice, tab]);

  if (!invoice) return null;

  const handleAction = () => {
    if (!action) return;
    if (action === "REJETER" && !motif.trim()) {
      toast.error("Motif obligatoire pour un rejet");
      return;
    }
    onAction(invoice.id, action, motif);
    setAction("");
    setMotif("");
  };

  const handleSendMsg = () => {
    if (!msgContent.trim()) return;
    onSendMessage(invoice.id, msgContent);
    setMsgContent("");
    setTimeout(() => {
      axios.get(`${API}/insurance/messages/${invoice.id}`, { headers: getHeaders() })
        .then(res => setMessages(res.data.messages || []))
        .catch(() => {});
    }, 500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-[#1e293b] border border-white/10 rounded-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <h2 className="text-lg font-bold text-white">{invoice.invoice_number || "Facture"}</h2>
            <p className="text-sm text-slate-400">{invoice.pharmacy_name} - {fmt(invoice.amount)} FCFA</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-xl font-bold">X</button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10">
          {["detail", "actions", "messages"].map(t => (
            <button key={t} onClick={() => setTab(t)} className={`flex-1 py-3 text-sm font-medium transition-colors ${tab === t ? "text-sky-400 border-b-2 border-sky-400" : "text-slate-500 hover:text-slate-300"}`}>
              {t === "detail" ? "Détails" : t === "actions" ? "Actions" : "Messages"}
            </button>
          ))}
        </div>

        <div className="p-5">
          {tab === "detail" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs text-slate-500 mb-1">Assureur</p><p className="text-white font-medium">{invoice.insurer_name}</p></div>
                <div><p className="text-xs text-slate-500 mb-1">Montant</p><p className="text-white font-bold">{fmt(invoice.amount)} FCFA</p></div>
                <div><p className="text-xs text-slate-500 mb-1">Statut</p><span className={`text-xs font-semibold px-2 py-1 rounded-full ${(STATUS_MAP[invoice.status] || {}).cls || "bg-slate-800 text-slate-400"}`}>{(STATUS_MAP[invoice.status] || {}).label || invoice.status}</span></div>
                <div><p className="text-xs text-slate-500 mb-1">Date</p><p className="text-slate-300">{new Date(invoice.created_at).toLocaleDateString("fr-FR")}</p></div>
                <div><p className="text-xs text-slate-500 mb-1">Pharmacie</p><p className="text-slate-300">{invoice.pharmacy_name}</p></div>
                <div><p className="text-xs text-slate-500 mb-1">Payé</p><p className="text-emerald-400 font-medium">{fmt(invoice.paid_amount || 0)} FCFA</p></div>
              </div>
              {invoice.rejection_reason && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                  <p className="text-xs text-red-400 font-semibold mb-1">Motif de rejet</p>
                  <p className="text-sm text-red-300">{invoice.rejection_reason}</p>
                </div>
              )}
            </div>
          )}

          {tab === "actions" && (
            <div className="space-y-4">
              <p className="text-sm text-slate-400">Selectionnez une action pour cette facture :</p>
              <div className="grid grid-cols-3 gap-3">
                <button data-testid="action-valider" onClick={() => setAction("VALIDER")} className={`p-4 rounded-xl border text-center transition-all ${action === "VALIDER" ? "border-emerald-500 bg-emerald-500/10" : "border-white/10 hover:border-emerald-500/30"}`}>
                  <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-emerald-400">Valider</p>
                </button>
                <button data-testid="action-rejeter" onClick={() => setAction("REJETER")} className={`p-4 rounded-xl border text-center transition-all ${action === "REJETER" ? "border-red-500 bg-red-500/10" : "border-white/10 hover:border-red-500/30"}`}>
                  <XCircle className="w-6 h-6 text-red-400 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-red-400">Rejeter</p>
                </button>
                <button data-testid="action-complement" onClick={() => setAction("DEMANDER_COMPLEMENT")} className={`p-4 rounded-xl border text-center transition-all ${action === "DEMANDER_COMPLEMENT" ? "border-amber-500 bg-amber-500/10" : "border-white/10 hover:border-amber-500/30"}`}>
                  <AlertTriangle className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-amber-400">Complement</p>
                </button>
              </div>
              {(action === "REJETER" || action === "DEMANDER_COMPLEMENT") && (
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">{action === "REJETER" ? "Motif de rejet (obligatoire)" : "Détails demandes"}</label>
                  <textarea data-testid="action-motif" value={motif} onChange={e => setMotif(e.target.value)} className="w-full bg-[#111827] border border-white/10 rounded-lg p-3 text-white text-sm min-h-[80px] focus:border-sky-500 focus:outline-none" placeholder="Indiquez le motif..." />
                </div>
              )}
              {action && (
                <Button data-testid="confirm-action-btn" onClick={handleAction} className="w-full bg-sky-500 hover:bg-sky-400 text-white font-semibold">
                  Confirmer l'action
                </Button>
              )}
            </div>
          )}

          {tab === "messages" && (
            <div className="space-y-4">
              <div className="max-h-60 overflow-y-auto space-y-3">
                {messages.length === 0 ? (
                  <p className="text-center text-slate-500 text-sm py-8">Aucun message. Demarrez la conversation.</p>
                ) : messages.map(m => (
                  <div key={m.id} className={`p-3 rounded-lg ${m.sender_role === "assurance" ? "bg-sky-500/10 ml-8" : "bg-slate-800 mr-8"}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-slate-300">{m.sender_name}</span>
                      <span className="text-[10px] text-slate-500">{new Date(m.created_at).toLocaleString("fr-FR")}</span>
                    </div>
                    <p className="text-sm text-slate-300">{m.content}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input data-testid="msg-input" value={msgContent} onChange={e => setMsgContent(e.target.value)} placeholder="Votre message..." className="flex-1 bg-[#111827] border-white/10 text-white" onKeyDown={e => e.key === "Enter" && handleSendMsg()} />
                <Button data-testid="send-msg-btn" onClick={handleSendMsg} className="bg-sky-500 hover:bg-sky-400"><Send className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function InsurancePortalPage() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      const res = await axios.get(`${API}/insurance/dashboard?${params}`, { headers: getHeaders() });
      setData(res.data);
    } catch { }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (invoiceId, action, motif) => {
    try {
      await axios.post(`${API}/insurance/invoices/${invoiceId}/action`, { action, motif }, { headers: getHeaders() });
      toast.success(`Facture ${action.toLowerCase()}e avec succès`);
      setSelected(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleSendMessage = async (invoiceId, content) => {
    try {
      await axios.post(`${API}/insurance/messages`, { invoice_id: invoiceId, content }, { headers: getHeaders() });
      toast.success("Message envoye");
    } catch (e) { toast.error("Erreur envoi message"); }
  };

  const stats = data?.stats || {};
  const invoices = (data?.invoices || []).filter(i =>
    !filter || (i.invoice_number || "").toLowerCase().includes(filter.toLowerCase()) ||
    (i.pharmacy_name || "").toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div data-testid="insurance-portal-page" className="min-h-screen bg-[#111827] text-white">
      {/* Top bar */}
      <div className="border-b border-white/10 bg-[#1e293b]/80 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-500/15 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-sky-400" />
            </div>
            <div>
              <h1 className="text-lg font-bold">Portail Assureur</h1>
              <p className="text-xs text-slate-400">{user?.company_name || user?.pharmacy_name || user?.email}</p>
            </div>
          </div>
          <button onClick={() => { logout(); window.location.href = "/login"; }} className="flex items-center gap-2 text-sm text-slate-400 hover:text-red-400 transition-colors" data-testid="ins-logout">
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-[#1e293b] border border-white/10 rounded-xl p-4">
            <FileText className="w-4 h-4 text-sky-400 mb-2" />
            <p className="text-2xl font-bold">{stats.total_invoices || 0}</p>
            <p className="text-xs text-slate-400">Total factures</p>
          </div>
          <div className="bg-[#1e293b] border border-amber-500/20 rounded-xl p-4">
            <Clock className="w-4 h-4 text-amber-400 mb-2" />
            <p className="text-2xl font-bold text-amber-400">{stats.pending || 0}</p>
            <p className="text-xs text-slate-400">En attente</p>
          </div>
          <div className="bg-[#1e293b] border border-emerald-500/20 rounded-xl p-4">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 mb-2" />
            <p className="text-2xl font-bold text-emerald-400">{stats.validated || 0}</p>
            <p className="text-xs text-slate-400">Validées</p>
          </div>
          <div className="bg-[#1e293b] border border-red-500/20 rounded-xl p-4">
            <XCircle className="w-4 h-4 text-red-400 mb-2" />
            <p className="text-2xl font-bold text-red-400">{stats.rejected || 0}</p>
            <p className="text-xs text-slate-400">Rejetées</p>
          </div>
          <div className="bg-[#1e293b] border border-sky-500/20 rounded-xl p-4">
            <TrendingUp className="w-4 h-4 text-sky-400 mb-2" />
            <p className="text-2xl font-bold text-sky-400">{stats.validation_rate || 0}%</p>
            <p className="text-xs text-slate-400">Taux validation</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input data-testid="search-invoices" value={filter} onChange={e => setFilter(e.target.value)} placeholder="Rechercher (N facture, pharmacie...)" className="pl-10 bg-[#1e293b] border-white/10 text-white" />
          </div>
          <select data-testid="status-filter" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="bg-[#1e293b] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
            <option value="">Tous les statuts</option>
            <option value="recue">Recues</option>
            <option value="deposee">Déposées</option>
            <option value="en_recouvrement">Recouvrement</option>
            <option value="payee">Validées</option>
            <option value="rejetee">Rejetées</option>
          </select>
          <Button onClick={load} variant="outline" className="border-white/10 text-slate-400">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Actualiser
          </Button>
        </div>

        {/* Invoice Table */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-10 h-10 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="bg-[#1e293b] border border-white/10 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-500 border-b border-white/10 bg-[#111827]/50">
                  <th className="text-left py-3 px-4">N° Facture</th>
                  <th className="text-left py-3 px-4">Pharmacie</th>
                  <th className="text-right py-3 px-4">Montant</th>
                  <th className="text-center py-3 px-4">Statut</th>
                  <th className="text-left py-3 px-4">Date</th>
                  <th className="text-center py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-12 text-slate-500">Aucune facture trouvee</td></tr>
                ) : invoices.map(inv => {
                  const st = STATUS_MAP[inv.status] || { label: inv.status, cls: "bg-slate-800 text-slate-400" };
                  return (
                    <tr key={inv.id} className="border-b border-white/5 hover:bg-white/5 cursor-pointer" onClick={() => setSelected(inv)} data-testid={`invoice-row-${inv.id}`}>
                      <td className="py-3 px-4 text-white font-medium">{inv.invoice_number || inv.id?.slice(0, 8)}</td>
                      <td className="py-3 px-4 text-slate-300">{inv.pharmacy_name}</td>
                      <td className="py-3 px-4 text-right text-white font-semibold">{fmt(inv.amount)} FCFA</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${st.cls}`}>{st.label}</span>
                      </td>
                      <td className="py-3 px-4 text-slate-400">{new Date(inv.created_at).toLocaleDateString("fr-FR")}</td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <button className="p-1.5 rounded-lg hover:bg-emerald-500/10 text-emerald-400" onClick={e => { e.stopPropagation(); handleAction(inv.id, "VALIDER", ""); }}><CheckCircle2 className="w-4 h-4" /></button>
                          <button className="p-1.5 rounded-lg hover:bg-sky-500/10 text-sky-400" onClick={e => { e.stopPropagation(); setSelected(inv); }}><MessageSquare className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      <InvoiceDetailModal invoice={selected} onClose={() => setSelected(null)} onAction={handleAction} onSendMessage={handleSendMessage} />
    </div>
  );
}
